import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postV2ProviderEligibilityBatch: build.mutation<
      PostV2ProviderEligibilityBatchApiResponse,
      PostV2ProviderEligibilityBatchApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/eligibility/batch`,
        method: "POST",
        body: queryArg.batchCheckRequest,
      }),
    }),
    getV2ProviderEligibilityBatchByBatchId: build.query<
      GetV2ProviderEligibilityBatchByBatchIdApiResponse,
      GetV2ProviderEligibilityBatchByBatchIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/eligibility/batch/${queryArg.batchId}`,
      }),
    }),
    getV2ProviderEncountersByEncounterIdEligibility: build.query<
      GetV2ProviderEncountersByEncounterIdEligibilityApiResponse,
      GetV2ProviderEncountersByEncounterIdEligibilityApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/encounters/${queryArg.encounterId}/eligibility`,
      }),
    }),
    postV2ProviderEncountersByEncounterIdEligibility: build.mutation<
      PostV2ProviderEncountersByEncounterIdEligibilityApiResponse,
      PostV2ProviderEncountersByEncounterIdEligibilityApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/encounters/${queryArg.encounterId}/eligibility`,
        method: "POST",
      }),
    }),
    postV2ProviderEncountersByEncounterIdEligibilityOverride: build.mutation<
      PostV2ProviderEncountersByEncounterIdEligibilityOverrideApiResponse,
      PostV2ProviderEncountersByEncounterIdEligibilityOverrideApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/encounters/${queryArg.encounterId}/eligibility/override`,
        method: "POST",
        body: queryArg.manualUpdateRequest,
      }),
    }),
    getV2ProviderPatientsByPatientIdEligibility: build.query<
      GetV2ProviderPatientsByPatientIdEligibilityApiResponse,
      GetV2ProviderPatientsByPatientIdEligibilityApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/eligibility`,
      }),
    }),
    postV2ProviderPatientsByPatientIdEligibility: build.mutation<
      PostV2ProviderPatientsByPatientIdEligibilityApiResponse,
      PostV2ProviderPatientsByPatientIdEligibilityApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/eligibility`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type PostV2ProviderEligibilityBatchApiResponse =
  /** status 200 OK */ BatchCheckResponse;
export type PostV2ProviderEligibilityBatchApiArg = {
  batchCheckRequest: BatchCheckRequest;
};
export type GetV2ProviderEligibilityBatchByBatchIdApiResponse =
  /** status 200 OK */ BatchProgressResponse;
export type GetV2ProviderEligibilityBatchByBatchIdApiArg = {
  batchId: string;
};
export type GetV2ProviderEncountersByEncounterIdEligibilityApiResponse =
  /** status 200 OK */ EligibilityCheckResponse;
export type GetV2ProviderEncountersByEncounterIdEligibilityApiArg = {
  encounterId: string;
};
export type PostV2ProviderEncountersByEncounterIdEligibilityApiResponse =
  /** status 200 OK */ EligibilityCheckResponse;
export type PostV2ProviderEncountersByEncounterIdEligibilityApiArg = {
  encounterId: string;
};
export type PostV2ProviderEncountersByEncounterIdEligibilityOverrideApiResponse =
  /** status 200 OK */ EligibilityCheckResponse;
export type PostV2ProviderEncountersByEncounterIdEligibilityOverrideApiArg = {
  encounterId: string;
  manualUpdateRequest: ManualUpdateRequest;
};
export type GetV2ProviderPatientsByPatientIdEligibilityApiResponse =
  /** status 200 OK */ EligibilityCheckResponse;
export type GetV2ProviderPatientsByPatientIdEligibilityApiArg = {
  patientId: string;
};
export type PostV2ProviderPatientsByPatientIdEligibilityApiResponse =
  /** status 200 OK */ EligibilityCheckResponse;
export type PostV2ProviderPatientsByPatientIdEligibilityApiArg = {
  patientId: string;
};
export type BatchCheckResponse = {
  actions_created: number;
  batch_id: string;
  discovery_actions_created?: number;
  estimated_completion_minutes: number;
  skipped_recently_checked: number;
  total_patients: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type BatchCheckRequest = {
  /** Number of days ahead to look for appointments */
  days_ahead?: number;
  /** Skip patients checked within this many hours */
  staleness_hours?: number;
};
export type BatchProgressResponse = {
  batch_id: string;
  completed: number;
  failed: number;
  pending: number;
  progress_pct: number;
  total: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type BillingInput = {
  last_payment_date?: string | null;
  patient_due_status: string;
  total_charge_cents: number;
  total_paid_cents: number;
};
export type VisitLimitWarning = {
  plan_name: string;
  reason: "managed_medicaid" | "marketplace" | "pattern_match";
};
export type EligibilityCheckResponse = {
  alerts?: string[];
  billing?: BillingInput | null;
  corrected_member_id?: string | null;
  coverage_status: string;
  eligibility_snapshot_id?: string | null;
  encounter_id?: string | null;
  expires_at?: string | null;
  financials?: {
    [key: string]: any;
  };
  has_coverage?: boolean;
  last_checked_at?: string | null;
  next_action?: string;
  next_action_owner?: string;
  patient_id: string;
  patient_insurance_coverage_id?: string | null;
  pinned_appointment_at?: string | null;
  pinned_appointment_id?: string | null;
  pinned_encounter_id?: string | null;
  plan_status?: string | null;
  readiness?:
    | ("verified" | "collect" | "collected" | "attention" | "blocked" | "info")
    | null;
  readiness_label?: string | null;
  service_tier?: string;
  source?: string;
  status: string;
  submitted_member_id?: string | null;
  summary: string;
  visit_limit_warning?: VisitLimitWarning | null;
};
export type ManualUpdateRequest = {
  /** New coverage status */
  coverage_status?: string | null;
  /** Financial field overrides (e.g. copayment_cents, deductible_cents) */
  financials?: {
    [key: string]: any;
  } | null;
  /** Verification method (portal or phone) */
  method?: string | null;
  /** Next action (none/portal_review/phone_verify/block) */
  next_action?: string | null;
  /** Next action owner (provider/biller/system) */
  next_action_owner?: string | null;
  /** Additional notes */
  notes?: string | null;
  /** Reason for the manual override */
  reason?: string | null;
  /** New status (eligible/needs_review/blocked) */
  status?: string | null;
  /** Updated summary text */
  summary?: string | null;
};
